import * as Dfns from 'date-fns/fp';
import Papa from 'papaparse';
import { CreditMemoDataRow } from '@blisspointmedia/bpm-types/dist/CreditMemos';
import { Header } from '@Components/StickyTable';

export interface CreditMemoRow extends CreditMemoDataRow {
  selected: boolean;
  // Client-only, mirrors `selected` -- kept on the row itself (rather than a separate id array)
  // so a PDF download starting/finishing only changes this row's `data` entry, not every header's
  // memoized renderer/CSV-builder (which BPMTable keys off the `headers` array identity).
  fetching?: boolean;
}

export const CreditMemoStatus = {
  ACCEPTED: 'Accepted',
  REJECTED: 'Rejected',
  SENT: 'Sent'
} as const;
export type CreditMemoStatusType = (typeof CreditMemoStatus)[keyof typeof CreditMemoStatus];

const REFERENCE_DATE_FORMAT = 'yyyy-MMM-dd';
const TERMINAL_STATUSES: string[] = [CreditMemoStatus.ACCEPTED, CreditMemoStatus.REJECTED];

// Used as a table `contentGetter`, which runs for every row on every filter/sort pass -- an
// unparseable `week` must degrade to a plain string instead of throwing (date-fns' `format`
// throws a RangeError on an invalid Date), or one bad row would take down the whole table build.
export const getCreditMemoReference = (row: CreditMemoRow): string => {
  const [year, month, day] = row.week.split('-');
  const referenceDate = new Date(Number(year), Number(month) - 1, Number(day));
  const formattedDate = Number.isNaN(referenceDate.getTime())
    ? row.week
    : Dfns.format(REFERENCE_DATE_FORMAT, referenceDate).toUpperCase();
  return `CM${row.company.toUpperCase()}_${row.network}_${row.avail}_${formattedDate}`;
};

// The Accept/Reject/Email Credit Memo actions all require exactly one selected row that hasn't
// already reached a terminal status. Returns why the action is disabled, or undefined when it's
// enabled.
export const getSelectionDisabledReason = (selectedRows: CreditMemoRow[]): string | undefined => {
  if (selectedRows.length !== 1) {
    return 'Select one row to continue';
  }
  const { status } = selectedRows[0];
  return TERMINAL_STATUSES.includes(status) ? `Already ${status}` : undefined;
};

// BPMTable's onCsvChange builds its CSV from every header with no way to opt a column out, so
// this strips the given (by header `name`) columns out of the already-built CSV string --
// matches what this page's own CSV export excluded (checkbox toggle, PDF download button)
// before it was rebuilt on BPMTable's built-in CSV builder.
export const stripCsvColumns = (csv: string, headers: Header[], excludeNames: string[]): string => {
  const excludeIndexes = new Set(
    headers.flatMap((header, index) => (excludeNames.includes(header.name ?? '') ? [index] : []))
  );
  const { data } = Papa.parse<string[]>(csv);
  const rows = data.map(row => row.filter((_cell, index) => !excludeIndexes.has(index)));
  return Papa.unparse(rows);
};
